/**
 * Priority fair outbound queue for admin-app HTTP (storm dual-defense G1).
 * Decides *when* a call may start; inFlightGets singleflight remains inside the slot.
 * Wave D — HTTP_STORM_DETECTED latch freezes starts atop this queue (no drain while latched).
 */

import {
  getHttpStormRetryAfterMs,
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from './httpStormDetectedLatch.js';

/** Hard cap on concurrent started requests (all lanes). */
export const GLOBAL_MAX_INFLIGHT = 6;
/**
 * Interactive may hold at most this many when any non-interactive waiter exists
 * (reserves K=1 global slot for auth/realtime/default).
 */
export const INTERACTIVE_MAX_INFLIGHT = 5;
/** Non-interactive waiters aged ≥ this become eligible for the reserved slot under interactive flood. */
export const LANE_AGE_BOOST_MS = 2000;

/** Hot-path min start interval (me / inbox / badge) — G7 B1. */
export const HOT_PATH_MIN_INTERVAL_MS = 2000;
/** Default lane min start interval — G7 budget. */
export const DEFAULT_MIN_INTERVAL_MS = 250;
/** Overflow retryAfterMs for interactive (no min interval beyond global). */
export const INTERACTIVE_OVERFLOW_RETRY_AFTER_MS = 250;

export const INTERACTIVE_QUEUE_DEPTH = 32;
export const NON_INTERACTIVE_QUEUE_DEPTH = 8;

export const ADMIN_OUTBOUND_QUEUE_OVERFLOW_CODE = 'ADMIN_OUTBOUND_QUEUE_OVERFLOW' as const;

export type AdminOutboundLane = 'interactive' | 'auth' | 'realtime' | 'default';

export class AdminOutboundFairQueueOverflowError extends Error {
  public readonly code = ADMIN_OUTBOUND_QUEUE_OVERFLOW_CODE;
  public readonly sourceKey: string;
  public readonly retryAfterMs: number;

  public constructor(sourceKey: string, retryAfterMs: number) {
    super(
      `Admin outbound fair queue overflow for ${sourceKey}. Retry after ${String(retryAfterMs)}ms.`,
    );
    this.name = 'AdminOutboundFairQueueOverflowError';
    this.sourceKey = sourceKey;
    this.retryAfterMs = retryAfterMs;
  }
}

const LANE_PRIORITY: readonly AdminOutboundLane[] = [
  'interactive',
  'auth',
  'realtime',
  'default',
];

const NON_INTERACTIVE_LANES: readonly AdminOutboundLane[] = ['auth', 'realtime', 'default'];

interface Waiter {
  readonly sourceKey: string;
  readonly lane: AdminOutboundLane;
  readonly enqueuedAt: number;
  readonly signal: AbortSignal | undefined;
  readonly onStart: (() => void) | undefined;
  readonly run: () => Promise<unknown>;
  readonly resolve: (value: unknown) => void;
  readonly reject: (reason: unknown) => void;
  abortHandler: (() => void) | undefined;
}

interface LaneState {
  /** RR order of sourceKeys that currently have waiters. */
  rrOrder: string[];
  /** Queued waiters per sourceKey (FIFO within source). */
  bySource: Map<string, Waiter[]>;
}

interface QueueState {
  lanes: Record<AdminOutboundLane, LaneState>;
  globalInflight: number;
  interactiveInflight: number;
  sourceInflight: Map<string, number>;
  lastStartAt: Map<string, number>;
  wakeTimer: ReturnType<typeof setTimeout> | undefined;
  nextWakeAt: number | undefined;
}

function createEmptyLane(): LaneState {
  return { rrOrder: [], bySource: new Map() };
}

function createInitialState(): QueueState {
  return {
    lanes: {
      interactive: createEmptyLane(),
      auth: createEmptyLane(),
      realtime: createEmptyLane(),
      default: createEmptyLane(),
    },
    globalInflight: 0,
    interactiveInflight: 0,
    sourceInflight: new Map(),
    lastStartAt: new Map(),
    wakeTimer: undefined,
    nextWakeAt: undefined,
  };
}

let state: QueueState = createInitialState();

/** Explicit storm freeze flag (synced from latch subscribe; pump also checks live latch). */
let stormFrozen = false;

/**
 * Wave D — freeze outbound starts while HTTP_STORM_DETECTED is latched.
 * Call from latch subscribe (and tests). Does not abort in-flight; blocks new starts.
 */
export function freezeAdminOutboundForStorm(frozen: boolean): void {
  const wasFrozen = stormFrozen;
  stormFrozen = frozen;
  if (frozen) {
    const remaining = getHttpStormRetryAfterMs();
    if (remaining > 0) {
      scheduleWakeAt(Date.now() + remaining);
    }
    return;
  }
  if (wasFrozen) {
    pump();
  }
}

/** True when storm latch (or explicit freeze) blocks fair-queue starts. */
export function isAdminOutboundFrozenForStorm(): boolean {
  // Live latch so TTL expiry unblocks without requiring notify-on-expire.
  if (isHttpStormLatched()) {
    return true;
  }
  if (stormFrozen) {
    stormFrozen = false;
  }
  return false;
}

/**
 * Deterministic client source key from method + path (plan § Client↔server source-key map).
 */
export function normalizeAdminOutboundSourceKey(method: string, path: string): string {
  const upperMethod = method.toUpperCase();

  let normalized = path;
  const queryIdx = normalized.indexOf('?');
  if (queryIdx >= 0) {
    normalized = normalized.slice(0, queryIdx);
  }
  const hashIdx = normalized.indexOf('#');
  if (hashIdx >= 0) {
    normalized = normalized.slice(0, hashIdx);
  }

  if (/^https?:\/\//i.test(normalized)) {
    try {
      normalized = new URL(normalized).pathname;
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      throw new Error(`Failed to parse absolute URL for outbound source key: ${detail}`);
    }
  }

  if (!normalized.startsWith('/')) {
    normalized = `/${normalized}`;
  }

  const tenantPrefixed = /^\/api\/v1\/([^/]+)\/(admin|dev)\//.exec(normalized);
  if (
    tenantPrefixed !== null &&
    tenantPrefixed[1] !== 'admin' &&
    tenantPrefixed[1] !== 'dev'
  ) {
    normalized = normalized.replace(/^\/api\/v1\/[^/]+/, '');
  } else if (normalized.startsWith('/api/v1/')) {
    normalized = normalized.slice('/api/v1'.length);
  } else if (
    normalized.startsWith('/api/admin') ||
    normalized.startsWith('/api/dev')
  ) {
    normalized = normalized.slice('/api'.length);
  }

  normalized = normalized.replace(/\/{2,}/g, '/');
  if (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return `${upperMethod}:${normalized}`;
}

export function laneForAdminOutboundSourceKey(sourceKey: string): AdminOutboundLane {
  const colon = sourceKey.indexOf(':');
  const method = colon >= 0 ? sourceKey.slice(0, colon) : sourceKey;
  if (method !== 'GET') {
    return 'interactive';
  }
  if (sourceKey === 'GET:/admin/me') {
    return 'auth';
  }
  if (sourceKey === 'GET:/admin/inbox' || sourceKey === 'GET:/admin/inbox/badge') {
    return 'realtime';
  }
  return 'default';
}

function queueDepthForLane(lane: AdminOutboundLane): number {
  return lane === 'interactive' ? INTERACTIVE_QUEUE_DEPTH : NON_INTERACTIVE_QUEUE_DEPTH;
}

export function minIntervalMsForSourceKey(sourceKey: string): number {
  const lane = laneForAdminOutboundSourceKey(sourceKey);
  if (lane === 'auth' || lane === 'realtime') {
    return HOT_PATH_MIN_INTERVAL_MS;
  }
  if (lane === 'default') {
    return DEFAULT_MIN_INTERVAL_MS;
  }
  return 0;
}

function overflowRetryAfterMs(sourceKey: string): number {
  const lane = laneForAdminOutboundSourceKey(sourceKey);
  if (lane === 'interactive') {
    return INTERACTIVE_OVERFLOW_RETRY_AFTER_MS;
  }
  return minIntervalMsForSourceKey(sourceKey);
}

function createAbortError(options?: { readonly neverStarted?: boolean }): Error {
  const neverStarted = options?.neverStarted === true;
  let err: Error;
  if (typeof DOMException !== 'undefined') {
    err = new DOMException('The operation was aborted.', 'AbortError');
  } else {
    err = new Error('The operation was aborted.');
    err.name = 'AbortError';
  }
  if (neverStarted) {
    Object.defineProperty(err, 'adminOutboundNeverStarted', {
      value: true,
      enumerable: true,
    });
  }
  return err;
}

/** True when fair-queue rejected because AbortSignal fired while still queued (no start). */
export function isAdminOutboundQueuedAbort(error: unknown): boolean {
  if (error == null || typeof error !== 'object') {
    return false;
  }
  const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
  if (name !== 'AbortError') {
    return false;
  }
  return (
    'adminOutboundNeverStarted' in error &&
    (error as { adminOutboundNeverStarted?: unknown }).adminOutboundNeverStarted === true
  );
}

function waitingCountForSource(lane: AdminOutboundLane, sourceKey: string): number {
  return state.lanes[lane].bySource.get(sourceKey)?.length ?? 0;
}

function hasAnyWaiters(lane: AdminOutboundLane): boolean {
  return state.lanes[lane].rrOrder.length > 0;
}

function hasNonInteractiveWaiters(): boolean {
  return NON_INTERACTIVE_LANES.some((lane) => hasAnyWaiters(lane));
}

function peekHeadWaiter(lane: AdminOutboundLane, sourceKey: string): Waiter | undefined {
  const queue = state.lanes[lane].bySource.get(sourceKey);
  return queue?.[0];
}

function enqueueWaiter(waiter: Waiter): void {
  const laneState = state.lanes[waiter.lane];
  let queue = laneState.bySource.get(waiter.sourceKey);
  if (!queue) {
    queue = [];
    laneState.bySource.set(waiter.sourceKey, queue);
    laneState.rrOrder.push(waiter.sourceKey);
  }
  queue.push(waiter);
}

function removeWaiterFromQueue(waiter: Waiter): boolean {
  const laneState = state.lanes[waiter.lane];
  const queue = laneState.bySource.get(waiter.sourceKey);
  if (!queue) {
    return false;
  }
  const idx = queue.indexOf(waiter);
  if (idx < 0) {
    return false;
  }
  queue.splice(idx, 1);
  if (queue.length === 0) {
    laneState.bySource.delete(waiter.sourceKey);
    const rrIdx = laneState.rrOrder.indexOf(waiter.sourceKey);
    if (rrIdx >= 0) {
      laneState.rrOrder.splice(rrIdx, 1);
    }
  }
  return true;
}

function rotateSourceToBack(lane: AdminOutboundLane, sourceKey: string): void {
  const laneState = state.lanes[lane];
  const rrIdx = laneState.rrOrder.indexOf(sourceKey);
  if (rrIdx < 0) {
    return;
  }
  laneState.rrOrder.splice(rrIdx, 1);
  if (laneState.bySource.has(sourceKey)) {
    laneState.rrOrder.push(sourceKey);
  }
}

function sourceInflightCount(sourceKey: string): number {
  return state.sourceInflight.get(sourceKey) ?? 0;
}

function canStartSource(sourceKey: string, now: number): boolean {
  if (sourceInflightCount(sourceKey) > 0) {
    return false;
  }
  const minInterval = minIntervalMsForSourceKey(sourceKey);
  if (minInterval <= 0) {
    return true;
  }
  const last = state.lastStartAt.get(sourceKey);
  if (last === undefined) {
    return true;
  }
  return now - last >= minInterval;
}

function msUntilSourceEligible(sourceKey: string, now: number): number {
  const minInterval = minIntervalMsForSourceKey(sourceKey);
  if (minInterval <= 0) {
    return 0;
  }
  const last = state.lastStartAt.get(sourceKey);
  if (last === undefined) {
    return 0;
  }
  return Math.max(0, minInterval - (now - last));
}

/**
 * Pick next RR-eligible waiter in a lane (head of each source, RR across sources).
 */
function pickRoundRobinEligible(
  lane: AdminOutboundLane,
  now: number,
): Waiter | undefined {
  const laneState = state.lanes[lane];
  if (laneState.rrOrder.length === 0) {
    return undefined;
  }
  for (let i = 0; i < laneState.rrOrder.length; i += 1) {
    const sourceKey = laneState.rrOrder[i];
    if (sourceKey === undefined) {
      continue;
    }
    if (!canStartSource(sourceKey, now)) {
      continue;
    }
    const head = peekHeadWaiter(lane, sourceKey);
    if (head) {
      return head;
    }
  }
  return undefined;
}

function pickAgedNonInteractive(now: number): Waiter | undefined {
  for (const lane of NON_INTERACTIVE_LANES) {
    const laneState = state.lanes[lane];
    for (const sourceKey of laneState.rrOrder) {
      const head = peekHeadWaiter(lane, sourceKey);
      if (!head) {
        continue;
      }
      if (now - head.enqueuedAt < LANE_AGE_BOOST_MS) {
        continue;
      }
      if (!canStartSource(sourceKey, now)) {
        continue;
      }
      return head;
    }
  }
  return undefined;
}

function clearWakeTimer(): void {
  if (state.wakeTimer !== undefined) {
    clearTimeout(state.wakeTimer);
    state.wakeTimer = undefined;
    state.nextWakeAt = undefined;
  }
}

function scheduleWakeAt(atMs: number): void {
  const now = Date.now();
  if (atMs <= now) {
    clearWakeTimer();
    queueMicrotask(() => {
      pump();
    });
    return;
  }
  if (state.nextWakeAt !== undefined && state.nextWakeAt <= atMs) {
    return;
  }
  clearWakeTimer();
  state.nextWakeAt = atMs;
  const delay = Math.max(0, atMs - now);
  state.wakeTimer = setTimeout(() => {
    state.wakeTimer = undefined;
    state.nextWakeAt = undefined;
    pump();
  }, delay);
}

function considerWakeForPending(now: number): void {
  let earliest: number | undefined;
  for (const lane of LANE_PRIORITY) {
    const laneState = state.lanes[lane];
    for (const sourceKey of laneState.rrOrder) {
      if (sourceInflightCount(sourceKey) > 0) {
        continue;
      }
      const wait = msUntilSourceEligible(sourceKey, now);
      if (wait <= 0) {
        continue;
      }
      const at = now + wait;
      if (earliest === undefined || at < earliest) {
        earliest = at;
      }
    }
    // Age-boost wake: non-interactive may become boost-eligible later
    if (lane !== 'interactive') {
      for (const sourceKey of laneState.rrOrder) {
        const head = peekHeadWaiter(lane, sourceKey);
        if (!head) {
          continue;
        }
        const boostAt = head.enqueuedAt + LANE_AGE_BOOST_MS;
        if (boostAt > now && (earliest === undefined || boostAt < earliest)) {
          earliest = boostAt;
        }
      }
    }
  }
  if (earliest !== undefined) {
    scheduleWakeAt(earliest);
  }
}

function detachAbort(waiter: Waiter): void {
  const { signal, abortHandler } = waiter;
  if (signal && abortHandler) {
    signal.removeEventListener('abort', abortHandler);
  }
}

function startWaiter(waiter: Waiter): void {
  const now = Date.now();
  const removed = removeWaiterFromQueue(waiter);
  if (!removed) {
    return;
  }
  detachAbort(waiter);
  rotateSourceToBack(waiter.lane, waiter.sourceKey);

  // Start consumed — update lastStart even if the in-flight call later aborts.
  state.lastStartAt.set(waiter.sourceKey, now);
  state.globalInflight += 1;
  state.sourceInflight.set(waiter.sourceKey, sourceInflightCount(waiter.sourceKey) + 1);
  if (waiter.lane === 'interactive') {
    state.interactiveInflight += 1;
  }

  const finish = (): void => {
    state.globalInflight = Math.max(0, state.globalInflight - 1);
    if (waiter.lane === 'interactive') {
      state.interactiveInflight = Math.max(0, state.interactiveInflight - 1);
    }
    const nextCount = sourceInflightCount(waiter.sourceKey) - 1;
    if (nextCount <= 0) {
      state.sourceInflight.delete(waiter.sourceKey);
    } else {
      state.sourceInflight.set(waiter.sourceKey, nextCount);
    }
    pump();
  };

  // Auth /me ceiling (and similar) must record here — not at enqueue.
  try {
    waiter.onStart?.();
  } catch (onStartError: unknown) {
    finish();
    waiter.reject(onStartError);
    return;
  }

  void Promise.resolve()
    .then(() => waiter.run())
    .then(
      (value) => {
        finish();
        waiter.resolve(value);
      },
      (reason: unknown) => {
        finish();
        waiter.reject(reason);
      },
    );
}

function pump(): void {
  if (isAdminOutboundFrozenForStorm()) {
    const remaining = getHttpStormRetryAfterMs();
    if (remaining > 0) {
      scheduleWakeAt(Date.now() + remaining);
    } else {
      clearWakeTimer();
    }
    return;
  }

  const now = Date.now();

  while (state.globalInflight < GLOBAL_MAX_INFLIGHT) {
    const nonInteractiveWaiting = hasNonInteractiveWaiters();
    const maxInteractive = nonInteractiveWaiting
      ? INTERACTIVE_MAX_INFLIGHT
      : GLOBAL_MAX_INFLIGHT;
    const interactiveAlsoWaiting = hasAnyWaiters('interactive');

    // Age boost (one dispatch, then re-evaluate): aged non-interactive claims the
    // reserved slot under interactive flood instead of starting another interactive.
    const aged = pickAgedNonInteractive(now);
    if (aged && interactiveAlsoWaiting) {
      startWaiter(aged);
      continue;
    }

    if (state.interactiveInflight < maxInteractive) {
      const interactiveNext = pickRoundRobinEligible('interactive', now);
      if (interactiveNext) {
        startWaiter(interactiveNext);
        continue;
      }
    }

    let startedNonInteractive = false;
    for (const lane of NON_INTERACTIVE_LANES) {
      const next = pickRoundRobinEligible(lane, now);
      if (next) {
        startWaiter(next);
        startedNonInteractive = true;
        break;
      }
    }
    if (startedNonInteractive) {
      continue;
    }

    break;
  }

  if (
    state.globalInflight < GLOBAL_MAX_INFLIGHT &&
    LANE_PRIORITY.some((lane) => hasAnyWaiters(lane))
  ) {
    considerWakeForPending(now);
  } else {
    clearWakeTimer();
  }
}

export interface ScheduleAdminOutboundOptions<T> {
  readonly method: string;
  readonly path: string;
  readonly signal?: AbortSignal;
  /** Invoked exactly when the fair-queue start is consumed (before `run`). */
  readonly onStart?: () => void;
  readonly run: () => Promise<T>;
}

/**
 * Schedule an outbound admin HTTP call through the priority fair queue.
 */
export function scheduleAdminOutbound<T>(opts: ScheduleAdminOutboundOptions<T>): Promise<T> {
  const sourceKey = normalizeAdminOutboundSourceKey(opts.method, opts.path);
  const lane = laneForAdminOutboundSourceKey(sourceKey);

  if (opts.signal?.aborted) {
    return Promise.reject(createAbortError({ neverStarted: true }));
  }

  const depth = queueDepthForLane(lane);
  if (waitingCountForSource(lane, sourceKey) >= depth) {
    throw new AdminOutboundFairQueueOverflowError(sourceKey, overflowRetryAfterMs(sourceKey));
  }

  return new Promise<T>((resolve, reject) => {
    const waiter: Waiter = {
      sourceKey,
      lane,
      enqueuedAt: Date.now(),
      signal: opts.signal,
      onStart: opts.onStart,
      run: opts.run as () => Promise<unknown>,
      resolve: (value: unknown) => {
        resolve(value as T);
      },
      reject,
      abortHandler: undefined,
    };

    if (opts.signal) {
      const onAbort = (): void => {
        const wasQueued = removeWaiterFromQueue(waiter);
        detachAbort(waiter);
        if (wasQueued) {
          // Abort while queued: no lastStart update, no auth watermark (caller policy).
          reject(createAbortError({ neverStarted: true }));
          pump();
        }
      };
      waiter.abortHandler = onAbort;
      opts.signal.addEventListener('abort', onAbort, { once: true });
    }

    // Re-check depth under race of sync enqueues (same tick).
    if (waitingCountForSource(lane, sourceKey) >= depth) {
      detachAbort(waiter);
      reject(new AdminOutboundFairQueueOverflowError(sourceKey, overflowRetryAfterMs(sourceKey)));
      return;
    }

    enqueueWaiter(waiter);
    pump();
  });
}

/** Test-only: reset module queue state between cases. */
export function __resetAdminOutboundFairQueueForTests(): void {
  clearWakeTimer();
  stormFrozen = false;
  state = createInitialState();
}

// Wave D — latch bus drives freeze atop the fair-queue (no drain while latched).
subscribeHttpStormLatch(() => {
  freezeAdminOutboundForStorm(isHttpStormLatched());
});
freezeAdminOutboundForStorm(isHttpStormLatched());
